/**
 * ShareCardModal — preview the rendered front/back share cards, then either
 * download them as PNGs or push to the native share sheet (Web Share API,
 * which surfaces Instagram, Messages, WhatsApp, etc., on mobile).
 *
 * Flow:
 *   1. Caller passes { question, response, saveId? }.
 *   2. We POST /api/oracle/card-data to get the structured fields
 *      (tradition_a, tradition_b, shared_truth, truths[], short_id).
 *   3. We render <ShareCardFront /> and <ShareCardBack /> at 1080×1920 in a
 *      hidden, off-screen wrapper, and at 1080×1350 (4:5) in another wrapper.
 *   4. User taps a button → html-to-image captures the relevant DOM node →
 *      we offer Web Share + Download.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import QRCode from "qrcode";
import { X, Download, Share2, Loader2, ClipboardCopy, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ShareCardFront, ShareCardBack, ShareCardInsight } from "./ShareCard";
import { getTraditionGlyph, renderGlyphForName, resolveTraditionSlug } from "../lib/traditionGlyphs";

/** Base origin the shared URL should point at. Uses the CURRENT origin so
 *  sharing from the preview environment stays testable in preview, and sharing
 *  from production points at production. Falls back to correlate.online for
 *  the exceedingly rare SSR / no-window path. */
function shareOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://correlate.online";
}

/** Build a share URL from a short id — resolved via the /c/{short_id} short-link route. */
function buildShareUrl(data, saveId) {
  const origin = shareOrigin();
  const sid = data?.short_id
    || (saveId ? saveId.replace(/^orsv_/, "").replace(/^sub_/, "") : null);
  if (!sid) return `${origin}/weekly`;
  return `${origin}/c/${sid}`;
}

/** Build a paste-ready caption for Instagram / Threads / X / WhatsApp / etc.
 *  - Line 1: the shared_truth or key_insight in curly quotes (scroll-stopper).
 *  - Line 2: subject or tradition_a ↔ tradition_b lead-in.
 *  - Line 3: short URL. Auto-linkifies wherever URLs auto-linkify; on IG the
 *    viewer just taps-hold + Copy Link. */
function buildCaption(data, url) {
  if (!data) return url;
  const isInsight = data.card_type === "insight";
  const line1raw = (isInsight ? data.key_insight : data.shared_truth) || "";
  const cleaned = line1raw.replace(/^["'\u201C\u201D\u2018\u2019]+|["'\u201C\u201D\u2018\u2019]+$/g, "").trim();
  const line1 = cleaned ? `\u201C${cleaned}\u201D` : "";
  let line2 = "";
  if (isInsight) {
    const s = data.subject || {};
    const name = (s.name || "").trim();
    const tag = (s.tag || "").trim();
    if (name && tag) line2 = `${name} — from ${tag}. A correlation from Correlate.`;
    else if (name) line2 = `${name} — a correlation from Correlate.`;
    else line2 = "A correlation from Correlate.";
  } else {
    const a = (data.tradition_a?.name || "").trim();
    const b = (data.tradition_b?.name || "").trim();
    if (a && b) line2 = `${a} \u2194 ${b} — a correlation from Correlate.`;
    else line2 = "A correlation from Correlate.";
  }
  const parts = [];
  if (line1) parts.push(line1);
  parts.push(line2);
  parts.push(`Read the full breakdown \u2193\n${url}`);
  return parts.join("\n\n");
}

/** Robust clipboard write — falls back to a temporary textarea + execCommand
 *  when navigator.clipboard is unavailable (older Safari, non-HTTPS previews). */
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return !!ok;
  } catch {
    return false;
  }
}

export default function ShareCardModal({ open, onClose, question, response, saveId, shortId }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [aspect, setAspect] = useState("9x16"); // "9x16" | "4x5"
  const [side, setSide] = useState("front");    // "front" | "back"
  const [busy, setBusy] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);

  const frontRef = useRef(null);
  const backRef = useRef(null);

  // Fetch the structured card fields once when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: payload } = await api.post("/oracle/card-data", {
          question, response, save_id: saveId,
        });
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Couldn't build share card. Try again.");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, question, response, saveId, onClose]);

  // Generate QR data URL. Falls back through several link targets so the QR
  // is always present (we want the back card to drive traffic, always). Uses
  // the current origin so QR codes generated in preview point at preview and
  // QR codes generated in production point at production.
  useEffect(() => {
    const origin = shareOrigin();
    const target = (() => {
      const sid = data?.short_id || shortId;
      if (sid) return `${origin}/c/${sid}`;
      if (saveId) return `${origin}/c/${saveId.replace(/^orsv_/, "").replace(/^sub_/, "")}`;
      return `${origin}/weekly`;
    })();
    QRCode.toDataURL(target, {
      errorCorrectionLevel: "H",
      margin: 1,
      width: 440,
      color: { dark: "#2d1a0a", light: "#f6e9cd" },
    }).then(setQrUrl).catch(() => setQrUrl(null));
  }, [data?.short_id, shortId, saveId]);

  // Glyphs for both traditions
  const cardProps = useMemo(() => {
    if (!data) return null;
    const handle = data.handle || user?.handle || "";
    // Human-readable short URL printed on the card. Uses the current
    // hostname so preview cards read `preview.emergentagent.com/c/...`
    // (long but honest) and production cards read `correlate.online/c/...`.
    const host = (() => {
      try { return new URL(shareOrigin()).host; }
      catch { return "correlate.online"; }
    })();
    const shortUrl = data.short_id ? `${host}/c/${data.short_id}` : host;
    // Prefer LLM-emitted canonical slug; fall back to keyword matching on
    // tradition name. Either way produces a real glyph (never the neutral
    // flame) when ANY of the slug/name/tag fields contains a known token.
    const glyphFor = (t) => {
      const slug = (t?.slug && resolveTraditionSlug(t.slug))
        || resolveTraditionSlug(t?.name)
        || resolveTraditionSlug(t?.tag);
      if (slug) {
        const Glyph = getTraditionGlyph(slug);
        return React.createElement(Glyph, { size: 56 });
      }
      return renderGlyphForName(t?.name || t?.tag, { size: 56 });
    };
    const cardType = data.card_type === "insight" ? "insight" : "comparison";
    const baseProps = {
      handle,
      number: data.number || "001",
      shortUrl,
      qrDataUrl: qrUrl,
      question: data.question || question || "",
      truths: data.truths || [],
      cardType,
    };
    if (cardType === "insight") {
      return {
        ...baseProps,
        subject: {
          name: data.subject?.name || "—",
          tag:  data.subject?.tag  || "Tradition",
          essence: data.subject?.essence || "",
          glyph: glyphFor(data.subject),
        },
        keyInsight: data.key_insight || "",
      };
    }
    return {
      ...baseProps,
      traditionA: {
        name: data.tradition_a?.name || "—",
        tag:  data.tradition_a?.tag  || "Tradition A",
        essence: data.tradition_a?.essence || "",
        glyph: glyphFor(data.tradition_a),
      },
      traditionB: {
        name: data.tradition_b?.name || "—",
        tag:  data.tradition_b?.tag  || "Tradition B",
        essence: data.tradition_b?.essence || "",
        glyph: glyphFor(data.tradition_b),
      },
      sharedTruth: data.shared_truth || "",
    };
  }, [data, qrUrl, user, question]);

  const captureNode = async (node) => {
    if (!node) throw new Error("Card not rendered");
    // Wait one frame so fonts settle.
    await new Promise((r) => requestAnimationFrame(() => r()));
    const dim = aspect === "9x16" ? { width: 1080, height: 1920 } : { width: 1080, height: 1350 };
    return toPng(node, {
      ...dim,
      pixelRatio: 1,
      cacheBust: true,
      backgroundColor: "#f6e9cd",
    });
  };

  const filename = () => {
    const base = (data?.short_id || saveId || "correlation").toString().slice(0, 32);
    return `correlate-${base}-${side}-${aspect}.png`;
  };

  // Precomputed share URL — used by every share/copy action so users can
  // always get back to the correlation from the platform they landed on.
  const shareUrl = useMemo(() => buildShareUrl(data, saveId), [data, saveId]);

  const onCopyCaption = async () => {
    const ok = await copyToClipboard(buildCaption(data, shareUrl));
    if (ok) toast.success("Caption copied — ready to paste.");
    else toast.error("Could not copy caption.");
  };

  const onCopyLink = async () => {
    // Strict: only the URL, no whitespace, no surrounding text.
    const pureUrl = (shareUrl || "").trim();
    const ok = await copyToClipboard(pureUrl);
    if (ok) toast.success("Link copied.");
    else toast.error("Could not copy link.");
  };

  const onDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const node = side === "front" ? frontRef.current : backRef.current;
      const dataUrl = await captureNode(node);
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename();
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Card downloaded.");
      tickShare();
    } catch (e) {
      toast.error("Could not render card.");
      console.error(e);
    } finally { setBusy(false); }
  };

  const onShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const node = side === "front" ? frontRef.current : backRef.current;
      const dataUrl = await captureNode(node);
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename(), { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        // Pass `url` so platforms that honour it (iMessage, X, Threads,
        // WhatsApp) can render a tappable link chip. We deliberately do NOT
        // touch the clipboard here — the explicit "Copy caption" / "Copy link"
        // buttons are the only paths to the clipboard so the user's paste is
        // always exactly what they asked for.
        await navigator.share({
          files: [file],
          title: "Correlate",
          text: data?.shared_truth
            ? `\u201C${data.shared_truth}\u201D`
            : (data?.key_insight ? `\u201C${data.key_insight}\u201D` : "A correlation from Correlate."),
          url: shareUrl,
        });
        tickShare();
      } else {
        // Fallback: just download.
        const a = document.createElement("a");
        a.href = dataUrl; a.download = filename();
        document.body.appendChild(a); a.click(); a.remove();
        toast("Saved — your browser doesn't support direct sharing yet.");
        tickShare();
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Could not share.");
        console.error(e);
      }
    } finally { setBusy(false); }
  };

  // Fire-and-forget. Only ticks if we have a saveId (the post exists in Mongo).
  const tickShare = () => {
    if (!saveId) return;
    api.post(`/oracle/saves/${saveId}/share-tick`).catch(() => { /* silent */ });
  };

  // Capture BOTH front and back as PNGs and push them through Web Share as a
  // multi-file payload. Instagram, WhatsApp, Messages, and the iOS/Android
  // share sheets accept multi-image carousels — IG turns these into a
  // swipeable post automatically when the user picks "Feed".
  const onShareBoth = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Render front then back. Capture sequentially so each settles before
      // toPng reads its layout.
      const frontUrl = await captureNode(frontRef.current);
      const backUrl  = await captureNode(backRef.current);
      const baseName = (data?.short_id || saveId || "correlation").toString().slice(0, 32);
      const [fb, bb] = await Promise.all([
        fetch(frontUrl).then((r) => r.blob()),
        fetch(backUrl).then((r) => r.blob()),
      ]);
      const files = [
        new File([fb], `correlate-${baseName}-1-front-${aspect}.png`, { type: "image/png" }),
        new File([bb], `correlate-${baseName}-2-back-${aspect}.png`,  { type: "image/png" }),
      ];
      if (navigator.canShare && navigator.canShare({ files })) {
        // Deliberately do NOT touch the clipboard here — the "Copy caption"
        // / "Copy link" buttons are the only paths to the clipboard so the
        // user's paste is always exactly what they asked for.
        await navigator.share({
          files,
          title: "Correlate",
          text: data?.shared_truth
            ? `\u201C${data.shared_truth}\u201D`
            : (data?.key_insight ? `\u201C${data.key_insight}\u201D` : "A correlation from Correlate."),
          url: shareUrl,
        });
        tickShare();
      } else {
        // Fallback: download both as separate PNGs.
        for (const f of files) {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(f);
          a.download = f.name;
          document.body.appendChild(a); a.click(); a.remove();
          // Yield so the browser doesn't deduplicate downloads.
          await new Promise((r) => setTimeout(r, 200));
        }
        toast("Both images downloaded. Upload as a carousel post.");
        tickShare();
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Could not share both cards.");
        console.error(e);
      }
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return createPortal(
    <div
      data-testid="share-card-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-md bg-[hsl(40_30%_96%)] border border-[hsl(35_15%_85%)] rounded-lg shadow-2xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(35_15%_85%)]">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(38_60%_38%)]">Share card</div>
            <div className="serif text-xl text-[hsl(30_15%_20%)]">
              {data?.card_type === "insight" ? "Spread the insight" : "Spread the correlation"}
            </div>
          </div>
          <button
            data-testid="share-card-close"
            onClick={onClose}
            className="text-[hsl(30_15%_20%)] hover:opacity-70 transition-opacity"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Aspect + Side toggles */}
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex border border-[hsl(35_15%_75%)] rounded overflow-hidden">
              <button
                data-testid="share-card-side-front"
                onClick={() => setSide("front")}
                className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 transition-colors ${side === "front" ? "bg-[hsl(38_60%_48%)] text-white" : "bg-transparent text-[hsl(30_15%_30%)]"}`}
              >Front</button>
              <button
                data-testid="share-card-side-back"
                onClick={() => setSide("back")}
                className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 transition-colors ${side === "back" ? "bg-[hsl(38_60%_48%)] text-white" : "bg-transparent text-[hsl(30_15%_30%)]"}`}
              >Back</button>
            </div>
            <div className="inline-flex border border-[hsl(35_15%_75%)] rounded overflow-hidden">
              <button
                data-testid="share-card-aspect-9x16"
                onClick={() => setAspect("9x16")}
                className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 transition-colors ${aspect === "9x16" ? "bg-[hsl(38_60%_48%)] text-white" : "bg-transparent text-[hsl(30_15%_30%)]"}`}
              >9:16</button>
              <button
                data-testid="share-card-aspect-4x5"
                onClick={() => setAspect("4x5")}
                className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 transition-colors ${aspect === "4x5" ? "bg-[hsl(38_60%_48%)] text-white" : "bg-transparent text-[hsl(30_15%_30%)]"}`}
              >4:5</button>
            </div>
          </div>

          {/* Preview — scaled-down version of the actual capture node */}
          <div className="relative w-full bg-[hsl(30_15%_18%)] rounded overflow-hidden flex items-center justify-center" style={{ aspectRatio: aspect === "9x16" ? "9 / 16" : "4 / 5" }}>
            {loading ? (
              <div className="flex flex-col items-center gap-3 text-[hsl(40_30%_92%)]">
                <Loader2 className="animate-spin" size={28} />
                <div className="mono text-[10px] uppercase tracking-[0.22em] opacity-80">Distilling the convergence…</div>
              </div>
            ) : cardProps ? (
              // Inline scale-to-fit preview. The capture nodes themselves
              // live in the hidden off-screen container further below.
              <ScaledPreview side={side} aspect={aspect} cardProps={cardProps} />
            ) : null}
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              data-testid="share-card-download"
              onClick={onDownload}
              disabled={loading || busy}
              className="mono text-[9px] uppercase tracking-[0.18em] px-3 py-3 border border-[hsl(35_15%_70%)] text-[hsl(30_15%_20%)] hover:border-[hsl(38_60%_48%)] hover:text-[hsl(38_60%_48%)] disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2"
              title="Download just this side as a PNG."
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Download
            </button>
            <button
              data-testid="share-card-share"
              onClick={onShare}
              disabled={loading || busy}
              className="mono text-[9px] uppercase tracking-[0.18em] px-3 py-3 border border-[hsl(38_60%_48%)] text-[hsl(38_60%_48%)] hover:bg-[hsl(38_60%_48%)/0.08] disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2"
              title="Share just this side to one app (IG Story, single Tweet, etc.)."
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
              Share this
            </button>
            <button
              data-testid="share-card-share-both"
              onClick={onShareBoth}
              disabled={loading || busy}
              className="mono text-[9px] uppercase tracking-[0.18em] px-3 py-3 bg-[hsl(38_60%_48%)] text-white hover:bg-[hsl(38_60%_42%)] disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2"
              title="Push BOTH cards as a 2-slide carousel (IG Feed, Twitter quote, etc.)"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
              Share both
            </button>
          </div>
          <p className="text-[12px] text-[hsl(30_15%_40%)] leading-relaxed">
            <b>Share both</b> ships the front + back as a 2-slide carousel — Instagram, WhatsApp, Messages,
            and most apps will accept it as a single post with two swipeable images. On desktop, the buttons
            download PNGs you can upload from your phone.
          </p>

          {/* PASTE-READY CAPTION + LINK — solves the Instagram problem where
              URLs in Feed captions aren't tappable. Every share/download also
              auto-copies the caption; these buttons are the explicit escape
              hatches for when the user wants to preview or grab the link on
              its own. */}
          <div className="pt-3 border-t border-[hsl(35_15%_85%)] space-y-2">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(30_15%_35%)]">
              Get people back to the correlation
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="share-card-copy-caption"
                onClick={onCopyCaption}
                disabled={loading}
                className="mono text-[9px] uppercase tracking-[0.18em] px-3 py-2.5 border border-[hsl(35_15%_70%)] text-[hsl(30_15%_20%)] hover:border-[hsl(38_60%_48%)] hover:text-[hsl(38_60%_48%)] disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2"
                title="Copy a paste-ready caption (quote + link) for Instagram, Threads, X, etc."
              >
                <ClipboardCopy size={13} />
                Copy caption
              </button>
              <button
                type="button"
                data-testid="share-card-copy-link"
                onClick={onCopyLink}
                disabled={loading}
                className="mono text-[9px] uppercase tracking-[0.18em] px-3 py-2.5 border border-[hsl(35_15%_70%)] text-[hsl(30_15%_20%)] hover:border-[hsl(38_60%_48%)] hover:text-[hsl(38_60%_48%)] disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2"
                title="Copy just the short link — good for DMs, bios, iMessage."
              >
                <LinkIcon size={13} />
                Copy link
              </button>
            </div>
            <div className="text-[11px] text-[hsl(30_15%_50%)] leading-relaxed pt-1">
              <b>On Instagram:</b> Feed captions don&apos;t make links tappable — the caption you just copied
              includes the URL and quote so people can copy it. For a tappable link, use IG Stories&apos;
              <span className="mono uppercase text-[10px] tracking-[0.14em]"> link sticker</span> after posting.
            </div>
          </div>
        </div>

        {/* HIDDEN CAPTURE NODES — full-size 1080×N. Positioned far off-screen so
            they never paint inside the modal layout but html-to-image can read
            their computed dimensions. The front node renders ShareCardInsight
            instead of ShareCardFront when card_type === "insight". */}
        {cardProps && (
          <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }}>
            <div
              ref={frontRef}
              style={{ width: 1080, height: aspect === "9x16" ? 1920 : 1350, overflow: "hidden" }}
            >
              {cardProps.cardType === "insight" ? (
                <ShareCardInsight
                  {...cardProps}
                  width={1080}
                  height={aspect === "9x16" ? 1920 : 1350}
                />
              ) : (
                <ShareCardFront
                  {...cardProps}
                  width={1080}
                  height={aspect === "9x16" ? 1920 : 1350}
                />
              )}
            </div>
            <div
              ref={backRef}
              style={{ width: 1080, height: aspect === "9x16" ? 1920 : 1350, overflow: "hidden" }}
            >
              <ShareCardBack
                {...cardProps}
                width={1080}
                height={aspect === "9x16" ? 1920 : 1350}
              />
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// In-modal preview — same component, downscaled via CSS transform.
function ScaledPreview({ side, aspect, cardProps }) {
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(0.3);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const update = () => {
      const w = wrapperRef.current?.clientWidth || 360;
      const targetW = 1080;
      setScale(w / targetW);
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, []);

  const h = aspect === "9x16" ? 1920 : 1350;
  const isInsight = cardProps?.cardType === "insight";
  const Card = side === "front"
    ? (isInsight ? ShareCardInsight : ShareCardFront)
    : ShareCardBack;
  return (
    <div ref={wrapperRef} className="w-full h-full overflow-hidden flex items-center justify-center">
      <div style={{
        width: 1080, height: h,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        position: "absolute",
        top: 0, left: 0,
      }}>
        <Card {...cardProps} width={1080} height={h} />
      </div>
    </div>
  );
}
